<template>
    <div>
        <h4 class="text-base mb-4">Details</h4>
        <div class="flex flex-col gap-6">
            <!-- start card status -->
            <div
                class="flex flex-col items-start self-stretch sm:flex-row sm:items-center sm:gap-6"
            >
                <div
                    class="flex pb-2 flex-col items-start gap-1 self-stretch sm:flex-1 sm:justify-center sm:pb-0"
                >
                    <p
                        class="text-sm font-medium text-slate-800 dark:text-slate-100 hidden sm:block"
                    >
                        Card status
                    </p>
                    <p
                        class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300 block sm:hidden"
                    >
                        Card status
                    </p>
                    <p class="text-sm text-slate-500 dark:text-slate-400 hidden sm:block">
                        Set your card status as open or closed
                    </p>
                </div>

                <div
                    class="flex items-start rounded bg-slate-100 dark:bg-slate-700 self-stretch"
                >
                    <button
                        type="button"
                        @click="handleCardStatusChange(true)"
                        :class="[
                            'flex flex-1 items-center justify-center gap-2 rounded px-4 py-3 text-sm font-medium transition-all duration-200 border',
                            localCard.card_status === 'open'
                                ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm'
                                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300',
                        ]"
                    >
                        <Icon icon="card-check" class="h-5 w-5" />
                        Open
                    </button>
                    <button
                        type="button"
                        @click="handleCardStatusChange(false)"
                        :class="[
                            'flex flex-1 items-center justify-center gap-2 rounded px-4 py-3 text-sm font-medium transition-all duration-200 border',
                            localCard.card_status === 'closed'
                                ? 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm'
                                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300',
                        ]"
                    >
                        <Icon icon="card-closed" class="h-5 w-5" />
                        Closed
                    </button>
                </div>
            </div>
            <!-- end card status -->

            <!-- start user/business entity select -->
            <EntityAssignment
                :localCard="localCard"
                :selectedCard="selectedCard"
                :users="users"
                :businesses="businesses"
                :wallet="wallet"
                :selectedUserName="selectedUserName"
                :selectedBusinessName="selectedBusinessName"
                :showUserDropdown="showUserDropdown"
                :showBusinessDropdown="showBusinessDropdown"
                @toggle-user-dropdown="toggleUserDropdown"
                @toggle-business-dropdown="toggleBusinessDropdown"
                @select-user="selectUser"
                @select-business="selectBusiness"
                @open-add-entity="$emit('open-add-entity')"
            />
            <!-- end user/business entity select -->

            <!-- dates row -->
            <div
                class="grid sm:grid-cols-2 grid-cols-1 auto-rows-auto gap-4 sm:gap-2"
            >
                <!-- start date opened -->
                <UiInput
                    id="date_opened"
                    name="date_opened"
                    placeholder="Enter date"
                    :error="errors.date_opened"
                    label="Date opened"
                    :options="{
                        icon: 'calendar',
                        iconWrapperClass:
                            'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                        hideLabel: true,
                    }"
                    :disabled="localCard.opened_long_ago"
                >
                    <template #label>
                        <label
                            for="date_opened"
                            class="block text-sm font-medium leading-tight mb-2"
                        >
                            <span
                                class="text-sm font-medium leading-5"
                                :class="
                                    localCard.opened_long_ago
                                        ? 'text-slate-400 dark:text-slate-500'
                                        : 'text-slate-600 dark:text-slate-300'
                                "
                            >
                                Date opened
                            </span>
                        </label>
                    </template>
                    <template #input>
                        <flat-pickr
                            id="date-opened"
                            :config="dateConfig"
                            class="date-picker flatpickr-input form-input active border shadow-sm !pr-9 !pl-[54px] w-full disabled:cursor-not-allowed"
                            v-model="computedDateOpened"
                            placeholder="Enter date"
                            :disabled="localCard.opened_long_ago"
                            @input="$emit('clear-error', 'date_opened')"
                        ></flat-pickr>
                    </template>
                </UiInput>
                <!-- end date opened -->

                <!-- start date closed -->
                <div class="col-span-1">
                    <UiInput
                        id="date_closed"
                        name="date_closed"
                        placeholder="Enter date"
                        label="Date closed"
                        :error="errors.date_closed"
                        :options="{
                            icon: 'calendar',
                            iconWrapperClass:
                                'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                            hideLabel: true,
                        }"
                        :disabled="localCard.card_status !== 'closed'"
                    >
                        <template #label>
                            <label
                                for="date_closed"
                                class="block text-sm font-medium leading-tight mb-2"
                            >
                                <span
                                    class="text-sm font-medium leading-5"
                                    :class="
                                        localCard.card_status !== 'closed'
                                            ? 'text-slate-400 dark:text-slate-500'
                                            : 'text-slate-600 dark:text-slate-300'
                                    "
                                >
                                    Date closed
                                </span>
                                <span
                                    class="text-sm font-normal leading-5"
                                    :class="
                                        localCard.card_status !== 'closed'
                                            ? 'text-slate-400 dark:text-slate-500'
                                            : 'text-slate-500 dark:text-slate-400'
                                    "
                                >
                                    (optional)
                                </span>
                            </label>
                        </template>
                        <template #input>
                            <flat-pickr
                                id="date-closed"
                                :config="dateConfig"
                                class="date-picker flatpickr-input form-input active border shadow-sm !pr-9 !pl-[54px] w-full disabled:cursor-not-allowed"
                                v-model="localCard.date_closed"
                                placeholder="Enter date"
                                :disabled="localCard.card_status !== 'closed'"
                                @input="$emit('clear-error', 'date_closed')"
                            ></flat-pickr>
                        </template>
                    </UiInput>
                </div>
                <!-- end date closed -->

                <!-- start opened long ago checkbox -->
                <div
                    v-show="!localCard.product_changed"
                    class="col-span-1 row-start-2 flex items-center"
                    key="opened_long_ago"
                >
                    <input
                        type="checkbox"
                        class="form-checkbox mr-1 cursor-pointer"
                        name="opened_long_ago"
                        value="1"
                        v-model="localCard.opened_long_ago"
                        :checked="selectedCard?.opened_long_ago"
                        id="opened_long_ago"
                        @change="handleOpenedLongAgoChange"
                    />
                    <label for="opened_long_ago" class="sr-only">
                        I'm not sure (it was a few years ago)
                    </label>
                    <span class="ml-2 leading-none text-sm">
                        I'm not sure (it was a few years ago)
                    </span>
                </div>
                <!-- end opened long ago checkbox -->
            </div>

            <!-- annual fee + credit limit row -->
            <TransitionGroup
                name="fade"
                tag="div"
                class="grid sm:grid-cols-2 grid-cols-1 auto-rows-auto gap-4 sm:gap-2"
            >
                <!-- start annual fee -->
                <div
                    class="col-span-1"
                    v-if="localCard.card"
                    key="annual_fee"
                >
                    <UiInput
                        id="annual_fee"
                        name="annual_fee"
                        :disabled="!localCard.user_managed_annual_fee"
                        :error="errors.annual_fee"
                        :options="{
                            icon: 'dollar',
                            iconWrapperClass:
                                'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                        }"
                    >
                        <template #label>
                            <label
                                for="annual_fee"
                                class="block text-sm font-medium leading-tight mb-2"
                            >
                                <span
                                    class="text-sm font-medium leading-5"
                                    :class="
                                        !localCard.user_managed_annual_fee
                                            ? 'text-slate-400 dark:text-slate-500'
                                            : 'text-slate-600 dark:text-slate-300'
                                    "
                                >
                                    Annual Fee
                                </span>
                            </label>
                        </template>
                        <template #input>
                            <input
                                type="number"
                                :disabled="!localCard.user_managed_annual_fee"
                                class="form-input w-full active border shadow-sm pr-2 !pl-[54px] disabled:cursor-not-allowed"
                                v-model="computedAnnualFee"
                                :placeholder="computedAnnualFee || '0'"
                                min="0"
                                step="1"
                                @input="$emit('clear-error', 'annual_fee')"
                            />
                        </template>
                    </UiInput>
                </div>
                <!-- end annual fee -->

                <!-- start override annual fee -->
                <div
                    v-if="localCard.card"
                    class="col-span-1 row-start-2 flex items-center"
                    key="override_annual_fee"
                >
                    <input
                        type="checkbox"
                        class="form-checkbox mr-1 cursor-pointer"
                        name="user_managed_annual_fee"
                        id="user_managed_annual_fee"
                        v-model="localCard.user_managed_annual_fee"
                        value="1"
                    />
                    <label for="user_managed_annual_fee" class="sr-only">
                        Override Annual Fee
                    </label>
                    <span class="text-sm ml-2"> Override Annual Fee </span>
                </div>
                <!-- end override annual fee -->

                <!-- start credit limit -->
                <div class="col-span-1" key="credit_limit">
                    <UiInput
                        id="credit_limit"
                        name="credit_limit"
                        :options="{
                            icon: 'dollar',
                            iconWrapperClass:
                                'border-r border-slate-200 dark:border-slate-700 h-full pr-3',
                        }"
                    >
                        <template #label>
                            <label
                                for="credit_limit"
                                class="block text-sm font-medium leading-tight mb-2"
                            >
                                <span
                                    class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300"
                                >
                                    Credit Limit
                                </span>
                            </label>
                        </template>
                        <template #input>
                            <VueNumberFormat
                                class="form-input w-full shadow-sm pr-9 !pl-[54px] disabled:cursor-not-allowed"
                                v-model:value="localCard.credit_limit"
                                :options="{
                                    precision: 2,
                                }"
                                placeholder="$XX,XXX..."
                                min="0"
                            ></VueNumberFormat>
                        </template>
                    </UiInput>
                </div>
                <!-- end credit limit -->
            </TransitionGroup>

            <!-- account type + last four row -->
            <TransitionGroup
                name="fade"
                tag="div"
                class="grid sm:grid-cols-2 grid-cols-1 gap-4 sm:gap-2"
            >
                <!-- start account type select -->
                <div class="col-span-1" key="account_type">
                    <UiInput
                        id="card-account-type"
                        name="account_type"
                        :error="errors.account_type"
                    >
                        <template #label>
                            <label
                                for="card-account-type"
                                class="block text-sm font-medium leading-tight mb-2"
                            >
                                <span
                                    class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300"
                                >
                                    Account type
                                </span>
                            </label>
                        </template>
                        <template #input>
                            <select
                                class="form-select w-full"
                                v-model="localCard.account_type"
                                @change="$emit('clear-error', 'account_type')"
                            >
                                <option value="">
                                    Select account type...
                                </option>
                                <option value="cardholder">Cardholder</option>
                                <option value="authorized_user">
                                    Authorized User
                                </option>
                            </select>
                        </template>
                    </UiInput>
                </div>
                <!-- end account type select -->

                <!-- start last 4 -->
                <div class="col-span-1" key="last_four">
                    <UiInput
                        id="last_four"
                        name="last_four"
                        label="Last four"
                        :error="errors.last_four"
                        :options="{
                            hideLabel: true,
                        }"
                    >
                        <template #label>
                            <label
                                for="last_four"
                                class="block text-sm font-medium leading-tight mb-2"
                            >
                                <span
                                    class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300"
                                >
                                    Last digits of card
                                </span>
                                <span
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                                >
                                    (optional)
                                </span>
                            </label>
                        </template>
                        <template #input>
                            <input
                                type="text"
                                class="form-input w-full"
                                name="last_four"
                                v-model="localCard.last_four"
                                placeholder="#####"
                                v-maska
                                data-maska="#####"
                            />
                        </template>
                    </UiInput>
                </div>
                <!-- end last 4 -->
            </TransitionGroup>

            <!-- start hide account -->
            <div class="flex justify-between">
                <span class="text-sm flex flex-col">
                    <p class="">Hide this account</p>
                    <p class="text-slate-500 dark:text-slate-400">
                        Did we import a non credit-card account, such as a
                        mortgage, loan, checking account, or something else that
                        you want to hide? Check this option and we will hide
                        this account.
                    </p>
                </span>
                <span class="flex items-center text-sm">
                    <p class="sr-only">
                        {{ localCard.ignore ? 'Yes' : 'No' }}
                    </p>
                    <Switch
                        :label="localCard.ignore ? 'Yes' : 'No'"
                        :value="localCard.ignore"
                        @input="localCard.ignore = $event"
                    />
                </span>
            </div>
            <!-- end hide account -->
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Switch from '@/components/ui/switch/switch.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import EntityAssignment from './EntityAssignment.vue';
import { vMaska } from 'maska/vue';
const props = defineProps({
    localCard: Object,
    selectedCard: Object,
    users: Array,
    businesses: Array,
    wallet: Object,
    errors: Object,
    selectedUserName: String,
    selectedBusinessName: String,
    showUserDropdown: Boolean,
    showBusinessDropdown: Boolean,
});

const emit = defineEmits([
    'clear-error',
    'open-add-entity',
    'toggle-user-dropdown',
    'toggle-business-dropdown',
    'select-user',
    'select-business',
]);

const dateConfig = {
    dateFormat: 'm/d/Y',
    maxDate: 'today',
};

const computedAnnualFee = computed({
    get() {
        return props.localCard.card && !props.localCard.user_managed_annual_fee
            ? props.localCard.card.annual_fee
            : props.localCard.annual_fee;
    },
    set(value) {
        props.localCard.annual_fee = value;
    },
});

const computedDateOpened = computed({
    get() {
        return props.localCard.opened_long_ago
            ? ''
            : (props.localCard.date_opened ?? null);
    },
    set(value) {
        props.localCard.date_opened = value;
    },
});

const handleCardStatusChange = (isOpen) => {
    if (isOpen) {
        props.localCard.card_status = 'open';
        props.localCard.date_closed = null;
    } else {
        props.localCard.card_status = 'closed';
    }
    emit('clear-error', 'card_status');
};

const handleOpenedLongAgoChange = () => {
    if (props.localCard.opened_long_ago) {
        props.localCard.date_opened = '';
    } else {
        props.localCard.date_opened =
            props.selectedCard?.date_opened || '';
    }
    emit('clear-error', 'date_opened');
};

const toggleUserDropdown = () => {
    emit('toggle-user-dropdown');
};

const toggleBusinessDropdown = () => {
    emit('toggle-business-dropdown');
};

const selectUser = (user) => {
    emit('select-user', user);
};

const selectBusiness = (business) => {
    emit('select-business', business);
};
</script>
